<script>
// Generic daily trend line — used by the transit-time report drill-down for the
// per-creation-date average. Values may contain nulls (days with no valid
// arrivals): spanGaps stays false so those days render as honest gaps instead
// of an interpolated line.
import {Line} from "vue-chartjs";

export default {
  extends: Line,
  props: {
    labels: {type: Array, default: () => []},
    values: {type: Array, default: () => []},
    label: {type: String, default: ''},
    color: {type: String, default: '#E11F26'},
    unit: {type: String, default: ''},
    // Optional second series on a right-hand axis, e.g. the arrival rate (%)
    // next to the average days, so a low recent average is readable as
    // "not everything arrived yet". {label, values, color, unit, max}
    secondary: {type: Object, default: null},
    // Additive: the defaults keep the existing pages exactly as they were.
    lineWidth: {type: Number, default: 3},
    yMax: {type: Number, default: null},
    yMin: {type: Number, default: null},
  },
  mounted() {
    this.draw();
  },
  watch: {
    labels() {
      this.draw();
    },
    values() {
      this.draw();
    },
    secondary() {
      this.draw();
    },
    yMin() {
      this.draw();
    },
  },
  methods: {
    draw() {
      this.renderChart({
        labels: this.labels,
        datasets: [
          {
            label: this.label,
            borderColor: this.color,
            backgroundColor: 'transparent',
            borderWidth: this.lineWidth,
            pointBackgroundColor: this.color,
            pointRadius: 2,
            fill: false,
            spanGaps: false,
            lineTension: 0.2,
            yAxisID: 'primary',
            unit: this.unit,
            data: this.values,
          },
        ].concat(this.secondary ? [{
          label: this.secondary.label,
          borderColor: this.secondary.color || '#2EA8D9',
          backgroundColor: 'transparent',
          pointBackgroundColor: this.secondary.color || '#2EA8D9',
          pointRadius: 2,
          fill: false,
          spanGaps: false,
          lineTension: 0.2,
          borderDash: [6, 4],
          yAxisID: 'secondary',
          unit: this.secondary.unit || '',
          data: this.secondary.values,
        }] : []),
      }, {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            id: 'primary',
            position: 'left',
            ticks: {
              beginAtZero: this.yMin === null,
              ...(this.yMin !== null ? {min: this.yMin} : {}),
              ...(this.yMax !== null ? {max: this.yMax} : {}),
              callback: (value) => value + (this.unit ? ' ' + this.unit : ''),
              fontColor: 'black',
              fontFamily: 'Phetsarath OT',
              fontSize: 10,
            },
          }].concat(this.secondary ? [{
            id: 'secondary',
            position: 'right',
            gridLines: {drawOnChartArea: false},
            ticks: {
              beginAtZero: true,
              max: this.secondary.max || 100,
              callback: (value) => value + (this.secondary.unit ? ' ' + this.secondary.unit : ''),
              fontColor: this.secondary.color || '#2EA8D9',
              fontFamily: 'Phetsarath OT',
              fontSize: 10,
            },
          }] : []),
          xAxes: [{
            ticks: {
              fontColor: 'black',
              fontFamily: 'Phetsarath OT',
              fontSize: 10,
              maxRotation: 90,
              minRotation: 45,
              autoSkip: true,
              autoSkipPadding: 8,
            },
          }],
        },
        tooltips: {
          enabled: true,
          callbacks: {
            label: (item, data) => {
              const set = data.datasets[item.datasetIndex] || {};
              const value = item.yLabel === null || item.yLabel === undefined ? '-' : item.yLabel;
              return (set.label || '') + ': ' + value + (set.unit ? ' ' + set.unit : '');
            },
          },
          titleFontFamily: 'Phetsarath OT',
          bodyFontFamily: 'Phetsarath OT',
        },
        legend: {
          display: false,
        },
        plugins: {
          // The globally-registered datalabels plugin would label every point.
          datalabels: {
            display: false,
          },
        },
      });
    },
  },
}
</script>
